import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Ban, Check, Copy, ExternalLink, QrCode, Receipt } from 'lucide-react';
import {
  CHARGE_BILLING_TYPES,
  CHARGE_BILLING_TYPE_LABELS,
  CHARGE_STATUS_LABELS,
  LIVE_CHARGE_STATUSES,
  formatCents,
  type Charge,
  type ChargeBillingType,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { StatusBadge } from '@/components/StatusBadge';
import { DateInput } from '@/components/DateInput';
import { useAuth } from '@/lib/auth-context';
import { CHARGE_TONE } from './cobrancas-shared';

/**
 * A cobrança dentro da parcela do contas a receber.
 *
 * Fica aqui, e não numa tela separada, porque é aqui que a pergunta nasce:
 * "esta parcela está em aberto — como eu mando o cliente pagar?". O link e o
 * PIX ficam à mão para colar no WhatsApp, que é o que o operador faz de fato.
 */
export function ChargesOfInstallment({
  installmentId,
  openCents,
  canCharge,
  onChanged,
}: {
  installmentId: string;
  openCents: number;
  canCharge: boolean;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [emitindo, setEmitindo] = useState(false);
  const [cancelando, setCancelando] = useState<Charge | null>(null);

  const query = useQuery({
    queryKey: ['charges', 'installment', installmentId],
    queryFn: () => api.get<{ data: Charge[] }>(`/financial-installments/${installmentId}/charges`),
  });

  const cobrancas = query.data?.data ?? [];
  const viva = cobrancas.find((c) => LIVE_CHARGE_STATUSES.includes(c.status));
  const recarregar = () => {
    void query.refetch();
    onChanged();
  };

  return (
    <div style={{ marginTop: 10 }}>
      {cobrancas.map((charge) => (
        <div
          key={charge.id}
          style={{
            borderTop: `1px solid ${T.line}`,
            paddingTop: 10,
            marginTop: 10,
            display: 'grid',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 12.5, color: T.inkSoft }}>
              {CHARGE_BILLING_TYPE_LABELS[charge.billingType]} ·{' '}
              <span className="mono">{formatCents(charge.valueCents)}</span> · vence{' '}
              {formatDate(charge.dueDate)}
            </span>
            <StatusBadge kind={CHARGE_TONE[charge.status]}>
              {CHARGE_STATUS_LABELS[charge.status]}
            </StatusBadge>
          </div>

          {charge.status === 'RECEBIDA' && charge.feeCents > 0 && (
            <div className="mono" style={{ fontSize: 12, color: T.inkFaint }}>
              taxa do gateway {formatCents(charge.feeCents)} · líquido{' '}
              {formatCents(charge.netValueCents)}
            </div>
          )}

          {LIVE_CHARGE_STATUSES.includes(charge.status) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {charge.invoiceUrl && (
                <Button
                  small
                  icon={ExternalLink}
                  onClick={() => window.open(charge.invoiceUrl!, '_blank', 'noopener,noreferrer')}
                >
                  Abrir fatura
                </Button>
              )}
              {charge.invoiceUrl && <BotaoCopiar rotulo="Copiar link" valor={charge.invoiceUrl} />}
              {charge.pixPayload && (
                <BotaoCopiar rotulo="Copiar PIX" valor={charge.pixPayload} icone={QrCode} />
              )}
              {can('cobrancas:excluir') && (
                <Button small variant="ghost" icon={Ban} onClick={() => setCancelando(charge)}>
                  Cancelar
                </Button>
              )}
            </div>
          )}

          {charge.cancelReason && (
            <div style={{ fontSize: 12, color: T.inkFaint }}>Motivo: {charge.cancelReason}</div>
          )}
        </div>
      ))}

      {/* Só uma cobrança viva por parcela — o botão some enquanto houver uma,
          em vez de deixar o operador tentar e levar erro.

          `isSuccess` e não `!viva`: enquanto a lista não chegou ela é vazia,
          e sem esperar o botão aparece por um instante mesmo havendo cobrança
          viva — o operador clica, e o erro que esta regra existe para evitar
          acontece do mesmo jeito. */}
      {query.isSuccess && !viva && canCharge && openCents > 0 && can('cobrancas:criar') && (
        <div style={{ marginTop: 10 }}>
          <Button small icon={Receipt} onClick={() => setEmitindo(true)}>
            Gerar cobrança
          </Button>
        </div>
      )}

      <EmitirCobranca
        open={emitindo}
        installmentId={installmentId}
        openCents={openCents}
        onClose={() => setEmitindo(false)}
        onDone={() => {
          setEmitindo(false);
          recarregar();
        }}
      />

      <CancelarCobranca
        charge={cancelando}
        onClose={() => setCancelando(null)}
        onDone={() => {
          setCancelando(null);
          recarregar();
        }}
      />
    </div>
  );
}

function BotaoCopiar({
  rotulo,
  valor,
  icone = Copy,
}: {
  rotulo: string;
  valor: string;
  icone?: typeof Copy;
}) {
  const [copiado, setCopiado] = useState(false);
  return (
    <Button
      small
      icon={copiado ? Check : icone}
      onClick={() => {
        void navigator.clipboard?.writeText(valor);
        setCopiado(true);
        window.setTimeout(() => setCopiado(false), 2000);
      }}
    >
      {copiado ? 'Copiado' : rotulo}
    </Button>
  );
}

function EmitirCobranca({
  open,
  installmentId,
  openCents,
  onClose,
  onDone,
}: {
  open: boolean;
  installmentId: string;
  openCents: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [billingType, setBillingType] = useState<ChargeBillingType>('ESCOLHA_DO_CLIENTE');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [aberto, setAberto] = useState(false);

  if (open !== aberto) {
    setAberto(open);
    if (open) {
      setBillingType('ESCOLHA_DO_CLIENTE');
      setDueDate('');
      setDescription('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<Charge>('/charges', body),
    onSuccess: onDone,
  });
  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Gerar cobrança"
      description={`Vai cobrar ${formatCents(openCents)} — o que ainda falta desta parcela`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                installmentId,
                billingType,
                ...(dueDate ? { dueDate } : {}),
                ...(description.trim() ? { description: description.trim() } : {}),
              })
            }
          >
            Gerar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 12 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field
          label="Forma de pagamento"
          htmlFor="ch-type"
          error={errors.billingType}
          hint="“O cliente escolhe” manda um link único com PIX, boleto e cartão"
        >
          <select
            id="ch-type"
            className="input"
            value={billingType}
            onChange={(event) => setBillingType(event.target.value as ChargeBillingType)}
          >
            {CHARGE_BILLING_TYPES.map((value) => (
              <option key={value} value={value}>
                {CHARGE_BILLING_TYPE_LABELS[value]}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Vencimento da cobrança"
          htmlFor="ch-due"
          error={errors.dueDate}
          hint="Em branco usa o vencimento da parcela — o normal"
        >
          <DateInput
            id="ch-due"
            className="input input--mono"
            value={dueDate}
            onChange={setDueDate}
          />
        </Field>

        <Field
          label="O que o cliente vê na fatura"
          htmlFor="ch-desc"
          hint="Em branco usa a descrição do lançamento"
        >
          <input
            id="ch-desc"
            className="input"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Mensalidade AMAI Pass · outubro"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

function CancelarCobranca({
  charge,
  onClose,
  onDone,
}: {
  charge: Charge | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [alvo, setAlvo] = useState<string | null>(null);

  if ((charge?.id ?? null) !== alvo) {
    setAlvo(charge?.id ?? null);
    setReason('');
  }

  const mutation = useMutation({
    mutationFn: () => api.post<Charge>(`/charges/${charge!.id}/cancel`, { reason: reason.trim() }),
    onSuccess: onDone,
  });

  return (
    <Modal
      open={charge !== null}
      onClose={onClose}
      title="Cancelar cobrança"
      description="A cobrança some para o cliente; a parcela continua em aberto"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Voltar
          </Button>
          <Button
            variant="danger"
            loading={mutation.isPending}
            disabled={reason.trim().length < 5}
            onClick={() => mutation.mutate()}
          >
            Cancelar cobrança
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 12 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <Field
          label="Motivo"
          htmlFor="ch-cancel-reason"
          hint="Fica na trilha de auditoria com seu nome"
        >
          <input
            id="ch-cancel-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Cliente pediu boleto com outro vencimento"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}
